import { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ShoppingCart,
  Truck,
  Receipt,
  Eye,
  Printer,
  Monitor,
  Search,
  RotateCcw,
  Package,
  Phone,
  Clock,
  RefreshCw,
  UserCheck,
  Calendar,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CompactStatCard,
  DataTable,
  PageHeader,
  StatsGrid,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import { useToast } from "@/hooks/use-toast";
import {
  useAdvanceStoreOrderMutation,
  useStoreDashboard,
  useStoreId,
  useStoreOrders,
  useOrderById,
} from "@/hooks/queries";
import {
  getStoreOrderAdvanceAction,
  mapOrderStatusToStoreUi,
} from "@/lib/mappers/store-mappers";
import { printThermalReceipt } from "@/lib/print-thermal-receipt";
import { getApiErrorMessage } from "@/services/httpClient";
import type { StoreOrderRow } from "@/types/store.ui";
import { DeliveryType, OrderStatus, PaymentStatus } from "@/types/enums";
import type { OrderFilterParams } from "@/types/order.dto";

const STATUS_TAB_OPTIONS = [
  { key: "all", label: "All Orders", status: undefined },
  { key: "new", label: "New", status: OrderStatus.Pending },
  { key: "confirmed", label: "Confirmed", status: OrderStatus.Confirmed },
  { key: "preparing", label: "Preparing", status: OrderStatus.Processing },
  { key: "ready", label: "Ready", status: OrderStatus.ReadyForPickup },
  { key: "dispatched", label: "Dispatched", status: OrderStatus.OutForDelivery },
  { key: "delivered", label: "Delivered", status: OrderStatus.Delivered },
  { key: "cancelled", label: "Cancelled", status: OrderStatus.Cancelled },
] as const;

const statusColor: Record<string, string> = {
  new: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
  confirmed: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  preparing: "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20",
  ready: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20",
  dispatched: "bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20",
  delivered: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
};

const StoreOrders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { storeId, isLoading: storeLoading } = useStoreId();
  const { toast } = useToast();

  // Filter states
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  // Debounce search input for backend filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Construct backend query filter
  const queryFilters: OrderFilterParams = useMemo(() => {
    const f: OrderFilterParams = { pageSize: 100 };
    if (debouncedSearch.trim()) {
      f.search = debouncedSearch.trim();
    }
    const matchedTab = STATUS_TAB_OPTIONS.find((t) => t.key === selectedStatus);
    if (matchedTab && matchedTab.status !== undefined) {
      f.status = matchedTab.status;
    }
    if (fromDate) f.fromDate = fromDate;
    if (toDate) f.toDate = toDate;
    return f;
  }, [debouncedSearch, selectedStatus, fromDate, toDate]);

  const {
    data: ordersData,
    isLoading: ordersLoading,
    isFetching: ordersFetching,
    refetch,
  } = useStoreOrders(storeId, queryFilters);

  const { data: stats } = useStoreDashboard(storeId);
  const advanceMutation = useAdvanceStoreOrderMutation(storeId ?? 0);

  const orders = ordersData?.items ?? [];

  // Reset filters handler
  const handleResetFilters = () => {
    setSearchInput("");
    setDebouncedSearch("");
    setSelectedStatus("all");
    setFromDate("");
    setToDate("");
  };

  const isFiltered = Boolean(
    searchInput.trim() ||
    selectedStatus !== "all" ||
    fromDate ||
    toDate
  );

  // Table setup
  const table = useDataTable<StoreOrderRow>({
    data: orders,
    pageSize: 20,
    searchKeys: ["orderId", "customerName", "customerMemberId", "customerPhone"],
    initialSort: { id: "orderId", direction: "desc" },
  });

  // Selected Order for Details Dialog
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(() => {
    const paramId = searchParams.get("orderId");
    return paramId ? Number(paramId) : null;
  });

  useEffect(() => {
    const paramId = searchParams.get("orderId");
    if (paramId) {
      setSelectedOrderId(Number(paramId));
    }
  }, [searchParams]);

  const handleOpenDetails = (orderId: number) => {
    setSelectedOrderId(orderId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("orderId", String(orderId));
      return next;
    });
  };

  const handleCloseDetails = () => {
    setSelectedOrderId(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("orderId");
      return next;
    });
  };

  const { data: selectedOrder, isLoading: selectedOrderLoading } = useOrderById(
    selectedOrderId ?? 0,
    selectedOrderId !== null
  );

  const handleAdvance = async (order: StoreOrderRow) => {
    if (!storeId) return;
    const action = getStoreOrderAdvanceAction(order.status);
    if (!action) return;

    try {
      await advanceMutation.mutateAsync({
        orderId: Number(order.id),
        currentStatus: order.status,
      });
      toast({
        title: "Order status updated",
        description: `${order.orderId} → updated successfully`,
      });
    } catch (error) {
      toast({
        title: "Failed to update order",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  // Stats counts
  const activeOrdersCount = orders.filter(
    (o) => !["delivered", "cancelled"].includes(o.status)
  ).length;
  const deliveredOrdersCount = orders.filter((o) => o.status === "delivered").length;

  // Columns definition
  const columns: DataTableColumn<StoreOrderRow>[] = [
    {
      id: "orderId",
      header: "Order ID",
      accessorKey: "orderId",
      sortable: true,
      cell: (order) => (
        <div className="flex flex-col">
          <button
            onClick={() => handleOpenDetails(Number(order.id))}
            className="text-left font-bold text-sm text-primary hover:underline hover:text-primary/80 transition-colors"
          >
            {order.orderId}
          </button>
          <span className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
            <Clock className="h-3 w-3" />
            {order.orderDate}
          </span>
        </div>
      ),
    },
    {
      id: "customerName",
      header: "Customer Name",
      accessorKey: "customerName",
      sortable: true,
      cell: (order) => (
        <div className="flex flex-col">
          <span className="font-semibold text-foreground text-sm">
            {order.customerName || "Customer"}
          </span>
          {order.customerPhone ? (
            <span className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <Phone className="h-3 w-3" />
              {order.customerPhone}
            </span>
          ) : (
            <span className="text-[11px] text-muted-foreground italic">No phone</span>
          )}
        </div>
      ),
    },
    {
      id: "customerMemberId",
      header: "Member / Customer ID",
      accessorKey: "customerMemberId",
      cell: (order) =>
        order.customerMemberId ? (
          <Badge
            variant="secondary"
            className="font-mono text-xs font-semibold px-2 py-0.5 bg-primary/10 text-primary border-primary/20 flex items-center gap-1 w-fit"
          >
            <UserCheck className="h-3 w-3" />
            <span>#{order.customerMemberId}</span>
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">Retail</span>
        ),
    },
    {
      id: "itemCount",
      header: "Total Items",
      accessorKey: "itemCount",
      align: "center",
      sortable: true,
      cell: (order) => (
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted/60 text-xs font-medium text-foreground">
          <Package className="h-3.5 w-3.5 text-muted-foreground" />
          <span>
            {order.itemCount} {order.itemCount === 1 ? "item" : "items"}
          </span>
        </div>
      ),
    },
    {
      id: "total",
      header: "Total Amount",
      accessorKey: "total",
      align: "right",
      sortable: true,
      cell: (order) => (
        <span className="font-bold text-sm text-foreground">
          ₹
          {order.total.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      id: "status",
      header: "Order Status",
      accessorKey: "status",
      align: "center",
      sortable: true,
      cell: (order) => (
        <Badge
          variant="outline"
          className={`capitalize font-medium text-xs px-2.5 py-0.5 ${
            statusColor[order.status] ?? "bg-muted"
          }`}
        >
          {order.status}
        </Badge>
      ),
    },
    {
      id: "payment",
      header: "Payment / Type",
      cell: (order) => {
        const isPaid =
          order.paymentStatus === PaymentStatus.Completed ||
          order.status === "delivered";
        const method =
          order.paymentMethod === "cod"
            ? "COD"
            : order.paymentMethod === "wallet"
              ? "Wallet"
              : "Online";

        return (
          <div className="flex flex-col items-start gap-1">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-foreground">{method}</span>
              <span
                className={`text-[10px] px-1.5 py-0.2 rounded font-semibold uppercase ${
                  isPaid
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400"
                    : "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400"
                }`}
              >
                {isPaid ? "Paid" : "Pending"}
              </span>
            </div>
            <span className="text-[11px] text-muted-foreground">
              {order.deliveryType === "self" ? "Self delivery" : "Platform"}
            </span>
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      align: "right",
      cell: (order) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleOpenDetails(Number(order.id))}
            className="h-8 gap-1.5 text-xs font-medium hover:bg-primary/5 hover:text-primary hover:border-primary/40 transition-colors"
          >
            <Eye className="h-3.5 w-3.5" />
            <span>View Details</span>
          </Button>
          {getStoreOrderAdvanceAction(order.status) && (
            <Button
              size="sm"
              className="h-8 text-xs font-medium gap-1"
              onClick={() => handleAdvance(order)}
              disabled={advanceMutation.isPending}
            >
              <span>{getStoreOrderAdvanceAction(order.status)}</span>
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Mobile card view
  const renderOrderCard = (order: StoreOrderRow) => {
    const advanceLabel = getStoreOrderAdvanceAction(order.status);
    const isPaid =
      order.paymentStatus === PaymentStatus.Completed ||
      order.status === "delivered";

    return (
      <div
        key={order.id}
        className="rounded-xl border border-border bg-card p-4 shadow-sm hover:shadow transition-shadow space-y-3.5"
      >
        {/* Header: Order ID & Status */}
        <div className="flex items-center justify-between gap-2 border-b border-border/60 pb-2.5">
          <button
            onClick={() => handleOpenDetails(Number(order.id))}
            className="text-left font-bold text-sm text-primary hover:underline"
          >
            {order.orderId}
          </button>
          <Badge
            variant="outline"
            className={`text-xs px-2.5 py-0.5 capitalize font-medium ${
              statusColor[order.status] ?? "bg-muted"
            }`}
          >
            {order.status}
          </Badge>
        </div>

        {/* Customer & Member info */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-semibold text-sm text-foreground">
              {order.customerName || "Customer"}
            </p>
            {order.customerPhone && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                <Phone className="h-3 w-3" /> {order.customerPhone}
              </p>
            )}
          </div>
          {order.customerMemberId ? (
            <Badge
              variant="secondary"
              className="font-mono text-[11px] font-semibold bg-primary/10 text-primary flex items-center gap-1"
            >
              <UserCheck className="h-3 w-3" />
              <span>#{order.customerMemberId}</span>
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground italic">Retail</span>
          )}
        </div>

        {/* Meta row: Date, Items, Amount, Payment */}
        <div className="grid grid-cols-2 gap-2 text-xs bg-muted/40 rounded-lg p-2.5">
          <div>
            <span className="text-muted-foreground block text-[11px]">Items</span>
            <span className="font-semibold text-foreground flex items-center gap-1 mt-0.5">
              <Package className="h-3.5 w-3.5 text-muted-foreground" />
              {order.itemCount} {order.itemCount === 1 ? "item" : "items"}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground block text-[11px]">Total</span>
            <span className="font-bold text-foreground text-sm mt-0.5 block">
              ₹
              {order.total.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          <div className="mt-1">
            <span className="text-muted-foreground block text-[11px]">Date</span>
            <span className="font-medium text-foreground">{order.orderDate}</span>
          </div>
          <div className="mt-1">
            <span className="text-muted-foreground block text-[11px]">Payment</span>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="font-medium text-foreground">
                {order.paymentMethod === "cod"
                  ? "COD"
                  : order.paymentMethod === "wallet"
                    ? "Wallet"
                    : "Online"}
              </span>
              <span
                className={`text-[10px] px-1 rounded font-semibold uppercase ${
                  isPaid
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400"
                    : "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400"
                }`}
              >
                {isPaid ? "Paid" : "Pending"}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-1">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-xs gap-1.5 h-9"
            onClick={() => handleOpenDetails(Number(order.id))}
          >
            <Eye className="h-3.5 w-3.5" />
            <span>View Details</span>
          </Button>
          {advanceLabel && (
            <Button
              size="sm"
              className="flex-1 text-xs h-9"
              onClick={() => handleAdvance(order)}
              disabled={advanceMutation.isPending}
            >
              {advanceLabel}
            </Button>
          )}
        </div>
      </div>
    );
  };

  if (storeLoading || (ordersLoading && !ordersData)) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-4 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  if (!storeId) {
    return (
      <PageHeader
        title="Orders"
        description="No store linked to your account."
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Orders"
        description="Manage store orders, track fulfillment and print receipts."
        actions={
          <Button variant="outline" className="gap-2" asChild>
            <Link to="/store/pos">
              <Monitor className="h-4 w-4" /> POS Billing
            </Link>
          </Button>
        }
      />

      {/* Overview Stat Cards */}
      <StatsGrid columns={3}>
        <CompactStatCard
          title="Active Orders"
          value={activeOrdersCount}
          icon={ShoppingCart}
        />
        <CompactStatCard
          title="Today's Orders"
          value={stats?.todayOrders ?? 0}
          icon={Truck}
        />
        <CompactStatCard
          title="Delivered Orders"
          value={deliveredOrdersCount}
          icon={Receipt}
        />
      </StatsGrid>

      {/* Status Filter Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
        {STATUS_TAB_OPTIONS.map((tab) => {
          const isSelected = selectedStatus === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setSelectedStatus(tab.key)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap border ${
                isSelected
                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                  : "bg-card text-muted-foreground border-border hover:bg-muted/70 hover:text-foreground"
              }`}
            >
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Filter & Search Bar */}
      <div className="rounded-xl border border-border bg-card p-4 shadow-sm space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
          {/* Search Input */}
          <div className="lg:col-span-5 space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Search Orders
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by Order ID, customer, member ID, phone..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9 h-9 text-xs"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput("")}
                  className="absolute right-2.5 top-2.5 text-xs text-muted-foreground hover:text-foreground"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* From Date */}
          <div className="lg:col-span-3 space-y-1">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" /> From Date
            </label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-9 text-xs"
            />
          </div>

          {/* To Date */}
          <div className="lg:col-span-3 space-y-1">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" /> To Date
            </label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-9 text-xs"
            />
          </div>

          {/* Actions: Reset & Refresh */}
          <div className="lg:col-span-1 flex items-center gap-1.5 justify-end sm:justify-start">
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetFilters}
              disabled={!isFiltered}
              className="h-9 px-3 text-xs gap-1 text-muted-foreground hover:text-foreground"
              title="Reset all filters"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              <span className="hidden sm:inline lg:hidden">Reset</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={ordersFetching}
              className="h-9 px-3 text-xs text-muted-foreground hover:text-foreground"
              title="Refresh order list"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${ordersFetching ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </div>

        {/* Filter status indicator */}
        {isFiltered && (
          <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t border-border/50">
            <span>
              Filters active • Showing{" "}
              <strong className="text-foreground">{orders.length}</strong> matching orders
            </span>
            <button
              onClick={handleResetFilters}
              className="text-primary hover:underline font-medium"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Orders Table */}
      <DataTable
        title={`All Orders (${orders.length})`}
        viewMode="cards"
        columns={columns}
        data={table.rows}
        getRowId={(row) => row.id}
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Quick filter in results..."
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        emptyMessage={
          isFiltered
            ? "No orders match the current filters. Try resetting the search or date range."
            : "No orders found for this store yet."
        }
        renderMobileCard={renderOrderCard}
      />

      {/* Professional Order Details Dialog */}
      <Dialog
        open={selectedOrderId !== null}
        onOpenChange={(open) => !open && handleCloseDetails()}
      >
        <DialogContent className="max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-3xl overflow-y-auto sm:w-full p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pr-6 pb-2 border-b border-border">
              <div>
                <span className="text-base sm:text-lg font-bold">
                  Order Details — #{selectedOrder?.orderNumber || "..."}
                </span>
                {selectedOrder && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Placed on{" "}
                    {new Date(selectedOrder.createdAt).toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                  </p>
                )}
              </div>
              {selectedOrder && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 text-xs"
                  onClick={() =>
                    printThermalReceipt(selectedOrder, { paperWidthMm: 80 })
                  }
                >
                  <Printer className="w-3.5 h-3.5" /> Print Receipt
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedOrderLoading && (
            <div className="space-y-4 py-6">
              <Skeleton className="h-6 w-1/3" />
              <div className="grid grid-cols-2 gap-4">
                <Skeleton className="h-24 rounded-lg" />
                <Skeleton className="h-24 rounded-lg" />
              </div>
              <Skeleton className="h-40 w-full rounded-lg" />
            </div>
          )}

          {!selectedOrderLoading && selectedOrder && (
            <div className="space-y-5 pt-2">
              {/* Status & Payment Overview Badges */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-muted/40 p-3.5 rounded-xl text-xs">
                <div>
                  <span className="text-muted-foreground block text-[11px]">
                    Status
                  </span>
                  <Badge
                    variant="outline"
                    className={`mt-1 capitalize text-xs ${
                      statusColor[mapOrderStatusToStoreUi(selectedOrder.status)] ??
                      "bg-muted"
                    }`}
                  >
                    {mapOrderStatusToStoreUi(selectedOrder.status)}
                  </Badge>
                </div>
                <div>
                  <span className="text-muted-foreground block text-[11px]">
                    Delivery Mode
                  </span>
                  <span className="font-semibold text-foreground block mt-1">
                    {selectedOrder.deliveryType === DeliveryType.StoreDelivery
                      ? "Store Self Delivery"
                      : "Platform Delivery"}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block text-[11px]">
                    Payment Method
                  </span>
                  <span className="font-semibold text-foreground block mt-1">
                    {selectedOrder.paymentMethodText}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block text-[11px]">
                    Payment Status
                  </span>
                  <Badge
                    variant="secondary"
                    className={`mt-1 text-xs uppercase ${
                      selectedOrder.paymentStatus === PaymentStatus.Completed
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400"
                        : "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400"
                    }`}
                  >
                    {selectedOrder.paymentStatusText}
                  </Badge>
                </div>
              </div>

              {/* Customer Info & Shipping Address Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                {/* Customer Info Card */}
                <div className="border border-border p-3.5 rounded-xl space-y-2 bg-card">
                  <div className="flex items-center justify-between">
                    <h4 className="font-bold text-sm text-foreground flex items-center gap-1.5">
                      <span>Customer Details</span>
                    </h4>
                  </div>
                  <div className="space-y-1 text-muted-foreground">
                    <p className="font-semibold text-foreground text-sm">
                      {selectedOrder.customerName || "Customer"}
                    </p>
                    {selectedOrder.customerPhone && (
                      <p className="flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{selectedOrder.customerPhone}</span>
                      </p>
                    )}
                    {selectedOrder.customerNotes && (
                      <div className="mt-2.5 p-2 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/40 text-amber-800 dark:text-amber-300 rounded-lg text-xs">
                        <strong>Note:</strong> {selectedOrder.customerNotes}
                      </div>
                    )}
                  </div>
                </div>

                {/* Shipping Address Card */}
                <div className="border border-border p-3.5 rounded-xl space-y-2 bg-card">
                  <h4 className="font-bold text-sm text-foreground">
                    Delivery Address
                  </h4>
                  <div className="space-y-0.5 text-muted-foreground">
                    <p className="font-semibold text-foreground">
                      {selectedOrder.shippingAddress?.fullName ||
                        selectedOrder.customerName}
                    </p>
                    <p>{selectedOrder.shippingAddress?.addressLine1}</p>
                    {selectedOrder.shippingAddress?.addressLine2 && (
                      <p>{selectedOrder.shippingAddress.addressLine2}</p>
                    )}
                    <p>
                      {selectedOrder.shippingAddress?.city},{" "}
                      {selectedOrder.shippingAddress?.state} -{" "}
                      {selectedOrder.shippingAddress?.pincode}
                    </p>
                    {selectedOrder.shippingAddress?.phone && (
                      <p className="pt-1 text-foreground font-medium flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        <span>{selectedOrder.shippingAddress.phone}</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Order Items Table */}
              <div className="border border-border rounded-xl overflow-hidden bg-card">
                <div className="bg-muted/50 px-4 py-2.5 border-b border-border flex items-center justify-between">
                  <h4 className="font-bold text-xs uppercase tracking-wider text-muted-foreground">
                    Order Items ({selectedOrder.items.length})
                  </h4>
                  <span className="text-xs text-muted-foreground">
                    {selectedOrder.items.reduce((acc, it) => acc + it.quantity, 0)}{" "}
                    total units
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-muted/20 text-muted-foreground border-b border-border">
                      <tr>
                        <th className="p-3 w-10 text-center">#</th>
                        <th className="p-3">Product Name</th>
                        <th className="p-3 text-center">Quantity</th>
                        <th className="p-3 text-right">Unit Price</th>
                        <th className="p-3 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {selectedOrder.items.map((item, idx) => (
                        <tr key={item.id} className="hover:bg-muted/30">
                          <td className="p-3 text-center text-muted-foreground">
                            {idx + 1}
                          </td>
                          <td className="p-3 font-semibold text-foreground">
                            {item.productName}
                            {item.variantInfo && (
                              <span className="block text-[11px] font-normal text-muted-foreground">
                                {item.variantInfo}
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-center font-medium">
                            {item.quantity}
                          </td>
                          <td className="p-3 text-right text-muted-foreground">
                            ₹
                            {item.unitPrice.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </td>
                          <td className="p-3 text-right font-bold text-foreground">
                            ₹
                            {item.totalPrice.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Financial Totals & Stage Advance Actions */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 border-t border-border pt-4">
                {/* Financial Summary */}
                <div className="text-xs space-y-1.5 w-full sm:w-72 bg-muted/30 p-3 rounded-xl border border-border/60">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotal</span>
                    <span>
                      ₹
                      {selectedOrder.subTotal.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Delivery Charge</span>
                    <span>
                      ₹
                      {selectedOrder.deliveryCharge.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  {selectedOrder.discountAmount > 0 && (
                    <div className="flex justify-between text-emerald-600 font-medium">
                      <span>Discount</span>
                      <span>
                        -₹
                        {selectedOrder.discountAmount.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  )}
                  {selectedOrder.taxAmount > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>Tax / GST</span>
                      <span>
                        ₹
                        {selectedOrder.taxAmount.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-sm border-t border-border pt-2 text-foreground">
                    <span>Grand Total</span>
                    <span className="text-primary text-base">
                      ₹
                      {selectedOrder.totalAmount.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                  {getStoreOrderAdvanceAction(
                    mapOrderStatusToStoreUi(selectedOrder.status)
                  ) && (
                    <Button
                      onClick={() => {
                        handleAdvance({
                          id: String(selectedOrder.id),
                          orderId: selectedOrder.orderNumber,
                          customerName: selectedOrder.customerName,
                          items: [],
                          itemCount: selectedOrder.items.length,
                          total: selectedOrder.totalAmount,
                          status: mapOrderStatusToStoreUi(selectedOrder.status),
                          deliveryType:
                            selectedOrder.deliveryType ===
                            DeliveryType.StoreDelivery
                              ? "self"
                              : "platform",
                          orderDate: "",
                          paymentMethod: "cod",
                        });
                        handleCloseDetails();
                      }}
                      disabled={advanceMutation.isPending}
                    >
                      {getStoreOrderAdvanceAction(
                        mapOrderStatusToStoreUi(selectedOrder.status)
                      )}
                    </Button>
                  )}
                  <Button variant="outline" onClick={handleCloseDetails}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreOrders;
